import React, { ReactNode } from 'react';

import { withTooltip, TOOLTIP_MODES } from 'moet-core';
import {
  Field as FieldCore,
  SwitchCheckbox,
  BorderlessDropdown as BorderlessDropdownCore,
  Typography,
  SpacerVertical,
  Link,
} from '@klarna/bubble-ui';

import { OnboardingField } from '@/bpc/components/OnboardingFieldComponent/OnboardingField';
import {
  EncryptedString,
  FieldOption,
  FieldType,
} from '@mjolnir/mf-fields-ts/src/types/Field.type';
import { EncryptedInput as EncryptedInputCore, Toggle as ToggleCore } from '@/components';
import { BusinessSizeComponent } from '@/bpc/overrides/fields/BusinessSizeComponent';
import { RadioButtonsComponent } from '@/bpc/overrides/fields/RadioButtonsComponent';
import { CardsSelector } from '@/bpc/components/CardsSelector';
import { BusinessConcepts } from '@mjolnir/mf-fields-ts/src/enm/BusinessConcepts';

import { FIELD_OVERRIDES } from '@/bpc/overrides/fields';
import { FieldOverride } from '@/bpc/types/FieldOverride';
import { BusinessSizeConfig } from '@mjolnir/mf-fields-ts/src/types/BusinessSizeConfig';
import { ArrayField } from '../ArrayField';
import { Message } from 'mage-i18n';

const Field = withTooltip(FieldCore);
const BorderlessDropdown = withTooltip(BorderlessDropdownCore);
const EncryptedInput = withTooltip(EncryptedInputCore);
// @ts-expect-error: FIXME
const Toggle = withTooltip(ToggleCore, { tooltipBottomOverride: '30px' });

type ComponentProps = {
  t: TranslatorFunction;
  tKey: TranslatorFunctionKey;
  field: OnboardingField;
  value: string | boolean | number | EncryptedString;
  onChange: (value: string | boolean | number | EncryptedString | string[]) => void;
  onBlur: (value: string | boolean | number | EncryptedString | string[]) => void;
  highlightMandatoryButEmptyFields: boolean;
};

type ComponentState = {
  value: string | boolean | number | EncryptedString | string[];
  errorMessage?: string;
};

export class OnboardingFieldComponent extends React.Component<ComponentProps, ComponentState> {
  constructor(props: ComponentProps) {
    super(props);

    let startValue = props.value;

    // set booleans to false when there is no value yet and don't appear in the overwrite
    const isNotOverwritten = FIELD_OVERRIDES[props.field.absolutePath] !== FieldOverride.YesNo;
    if (props.field.type === FieldType.Boolean && isNotOverwritten && props.value === undefined) {
      startValue = false;
      // set the false as a default value on the payload so the button will be active
      props.onBlur(startValue);
    }
    this.state = {
      value: startValue,
      errorMessage: undefined,
    };

    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChangeAndOnBlur = this.onChangeAndOnBlur.bind(this);
    this.onChangeArray = this.onChangeArray.bind(this);
  }

  componentDidUpdate(prevProps: ComponentProps): void {
    if (this.props.value !== prevProps.value) {
      this.setState({ value: this.props.value });
    }
  }

  onChange(value: string | boolean | number | EncryptedString | string[]): void {
    const validator = this.props.field.validator;
    const errorMessage =
      validator != null && !validator.callback(value) ? validator.errorMessage() : undefined;
    this.setState({ value, errorMessage });
    if (errorMessage == null) {
      this.props.onChange(value);
    }
  }

  onBlur(): void {
    const { errorMessage, value } = this.state;
    if (errorMessage == null) {
      this.props.onBlur(this.props.field.type === FieldType.Number ? Number(value) : value);
    }
  }

  onChangeAndOnBlur(value: any): void {
    this.onChange(value);
    this.props.onBlur(value);
  }

  onChangeArray(newArray: any[]): void {
    this.onChange(newArray);
    this.props.onBlur(newArray);
  }

  render(): ReactNode {
    const { field, t, tKey, highlightMandatoryButEmptyFields } = this.props;
    let { value, errorMessage } = this.state;
    let label = t(field.label) || field.fallbackLabel;
    const isMandatory = field.mandatory();
    const isApplicable = field.applicable();
    const customConfiguration = field.customConfiguration ? field.customConfiguration() : undefined;
    const isDisabled = field.disabled();
    const isValueEmpty =
      value == null || value === '' || (Array.isArray(value) && value.length === 0);
    const registrationCountry =
      field.payload?.business_information?.registration_country ||
      field.payload?.mor_business_information?.registered_address?.country;
    const tooltipText = field.tooltipText
      ? (registrationCountry && t(`${field.tooltipText}.${registrationCountry}`)) ||
        t(field.tooltipText) ||
        field.fallbackTooltip
      : undefined;
    const isMorFlow =
      field.payload &&
      field.payload.contract_onboarding_type &&
      ['MOR_ONBOARDING_ENTITY', 'MOR_ONBOARDING_GROUP'].includes(
        field.payload.contract_onboarding_type,
      );
    const link = (value: string) => {
      const href = t(`${field.label.replace('label', 'link')}`);
      return href ? (
        <Link href={href} target="_blank">
          {value}
        </Link>
      ) : (
        value
      );
    };

    // shouldn't add optional label to fields which became just inapplicable and fade out
    if (isApplicable && !isMandatory) label += ` (${t('products-fe.general.formFieldOptional')})`;

    if (isApplicable && isMandatory && highlightMandatoryButEmptyFields && isValueEmpty)
      errorMessage = label + `${t('products-fe.errors.mandatoryField')}`;

    if (FIELD_OVERRIDES.hasOwnProperty(field.absolutePath)) {
      switch (FIELD_OVERRIDES[field.absolutePath]) {
        case FieldOverride.YesNo:
          if (isMorFlow) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (
              <>
                <Typography
                  data-testid="exit-point-message"
                  textToken="text-style/text/labels/tall/regular"
                >
                  <Message
                    path={tKey(field.label)}
                    fallback={field.fallbackLabel}
                    params={{ link }}
                  />
                </Typography>
                <SpacerVertical spaceToken="space/300" />
                <Toggle
                  label={
                    field.absolutePath === 'business_information.should_auto_capture_orders'
                      ? label
                      : null
                  }
                  // @ts-expect-error: FIXME
                  tooltipText={tooltipText}
                  tooltipMode={TOOLTIP_MODES.ICON}
                  toggleValue={value as any}
                  toggleOnChange={this.onChangeAndOnBlur}
                  // @ts-expect-error: FIXME
                  error={errorMessage}
                />
                <SpacerVertical spaceToken="space/400" />
              </>
            );
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (
              <Toggle
                label={
                  field.absolutePath === 'business_information.should_auto_capture_orders'
                    ? label
                    : null
                }
                // @ts-expect-error: FIXME
                tooltipText={tooltipText}
                tooltipMode={TOOLTIP_MODES.ICON}
                toggleValue={value as any}
                toggleOnChange={this.onChangeAndOnBlur}
                // @ts-expect-error: FIXME
                error={errorMessage}
              />
            );
          }
        case FieldOverride.BusinessSizeComponent:
          return (
            <BusinessSizeComponent
              value={value as string}
              onChange={this.onChangeAndOnBlur}
              t={t}
              error={errorMessage}
              {...((customConfiguration as unknown) as BusinessSizeConfig)}
            />
          );
        case FieldOverride.BusinessConcepts:
          return (
            <CardsSelector
              value={value as BusinessConcepts}
              onChange={this.onChangeAndOnBlur}
              options={field?.enumOptions()}
              error={errorMessage}
              showErrorOnTop={!isValueEmpty}
            />
          );
        case FieldOverride.Radio:
          if (isMorFlow) {
            return (
              <>
                <Typography
                  data-testid="exit-point-message"
                  textToken="text-style/text/labels/tall/regular"
                >
                  <Message
                    path={tKey(field.label)}
                    fallback={field.fallbackLabel}
                    params={{ link }}
                  />
                </Typography>
                <SpacerVertical spaceToken="space/300" />
                <RadioButtonsComponent
                  value={value as string}
                  onChange={this.onChangeAndOnBlur}
                  error={errorMessage}
                  options={field?.enumOptions()}
                  t={t}
                />
                <SpacerVertical spaceToken="space/300" />
              </>
            );
          } else {
            return (
              <RadioButtonsComponent
                value={value as string}
                onChange={this.onChangeAndOnBlur}
                error={errorMessage}
                options={field?.enumOptions()}
                t={t}
              />
            );
          }
      }
    } else if (field.type === FieldType.Boolean) {
      return (
        // @ts-expect-error: FIXME
        <SwitchCheckbox checked={value} onChange={this.onChangeAndOnBlur} disabled={isDisabled}>
          {label}
        </SwitchCheckbox>
      );
    } else if (field.type === FieldType.Enum) {
      const options = OnboardingFieldComponent.enumOptionsToBubbleUi(
        t,
        field.enumOptions() as FieldOption[],
      );
      if (isMorFlow) {
        return (
          <>
            <Typography
              data-testid="exit-point-message"
              textToken="text-style/text/labels/tall/regular"
            >
              <Message path={tKey(field.label)} fallback={field.fallbackLabel} params={{ link }} />
            </Typography>
            <BorderlessDropdown
              label={errorMessage}
              error={errorMessage != null}
              // @ts-expect-error: FIXME
              value={value}
              // @ts-expect-error: FIXME
              tooltipText={tooltipText}
              onChange={this.onChangeAndOnBlur}
              options={[{ key: '', label: 'Pick one!', disabled: true, hidden: true }, ...options]}
              disabled={isDisabled}
            />
            <SpacerVertical spaceToken="space/400" />
          </>
        );
      } else {
        return (
          <BorderlessDropdown
            label={errorMessage || label}
            error={errorMessage != null}
            // @ts-expect-error: FIXME
            value={value}
            // @ts-expect-error: FIXME
            tooltipText={tooltipText}
            onChange={this.onChangeAndOnBlur}
            options={[{ key: '', label: 'Pick one!', disabled: true, hidden: true }, ...options]}
            disabled={isDisabled}
          />
        );
      }
    } else if (field.type === FieldType.ArrayOfEnum) {
      if (isMorFlow) {
        return (
          <>
            <Typography
              data-testid="exit-point-message"
              textToken="text-style/text/labels/tall/regular"
            >
              <Message path={tKey(field.label)} fallback={field.fallbackLabel} params={{ link }} />
            </Typography>
            <SpacerVertical spaceToken="space/200" />
            <ArrayField
              label={errorMessage || ''}
              error={errorMessage != null}
              max={field.enumLimits?.max || Number.MAX_SAFE_INTEGER}
              min={field.enumLimits?.min || 0}
              options={OnboardingFieldComponent.enumOptionsToBubbleUiForArrayField(
                t,
                field.enumOptions() as FieldOption[],
              )}
              onChange={this.onChangeArray}
              value={value}
              disabled={isDisabled}
              contractOnboardingType={field.payload.contract_onboarding_type}
              absolutePath={field.absolutePath}
            />
            <SpacerVertical spaceToken="space/400" />
          </>
        );
      } else {
        return (
          <ArrayField
            label={label}
            max={field.enumLimits?.max || Number.MAX_SAFE_INTEGER}
            min={field.enumLimits?.min || 0}
            options={OnboardingFieldComponent.enumOptionsToBubbleUiForArrayField(
              t,
              field.enumOptions() as FieldOption[],
            )}
            onChange={this.onChangeArray}
            value={value}
            disabled={isDisabled}
          />
        );
      }
    } else if (field.type === FieldType.Date) {
      return (
        <Field
          // @ts-expect-error: FIXME
          value={value}
          label={errorMessage || label}
          // @ts-expect-error: FIXME
          tooltipText={tooltipText}
          error={errorMessage != null}
          onChange={this.onChange}
          // @ts-expect-error: FIXME
          onBlur={this.onBlur}
          isUnderlined
          type="date"
          disabled={isDisabled}
        />
      );
    } else if (field.type === FieldType.EncryptedString) {
      return (
        <EncryptedInput
          // @ts-expect-error: FIXME
          value={value as EncryptedString}
          // @ts-expect-error: FIXME
          tooltipText={tooltipText}
          label={errorMessage || label}
          error={errorMessage != null}
          onChange={this.onChange}
          // @ts-expect-error: FIXME
          onBlur={this.onBlur}
          isUnderlined
          disabled={isDisabled}
        />
      );
    } else {
      const type = { [FieldType.String]: 'text', [FieldType.Number]: 'number' }[field.type];

      if (isMorFlow) {
        return (
          <>
            <Typography
              data-testid="exit-point-message"
              textToken="text-style/text/labels/tall/regular"
            >
              <Message path={tKey(field.label)} fallback={field.fallbackLabel} params={{ link }} />
            </Typography>
            <Field
              // @ts-expect-error: FIXME
              value={value}
              // @ts-expect-error: FIXME
              tooltipText={tooltipText}
              label={errorMessage || ''}
              error={errorMessage != null}
              onChange={this.onChange}
              // @ts-expect-error: FIXME
              onBlur={this.onBlur}
              isUnderlined
              type={type}
              disabled={isDisabled}
            />
            <SpacerVertical spaceToken="space/400" />
          </>
        );
      } else {
        return (
          <Field
            // @ts-expect-error: FIXME
            value={value}
            // @ts-expect-error: FIXME
            tooltipText={tooltipText}
            label={errorMessage || label}
            error={errorMessage != null}
            onChange={this.onChange}
            // @ts-expect-error: FIXME
            onBlur={this.onBlur}
            isUnderlined
            type={type}
            disabled={isDisabled}
          />
        );
      }
    }
  }

  static sortByLabel(a: Record<string, string>, b: Record<string, string>): number {
    const x = a.label.toLowerCase();
    const y = b.label.toLowerCase();
    if (x > y) {
      return 1;
    }
    if (x < y) {
      return -1;
    }
    return 0;
  }

  static enumOptionsToBubbleUi(
    translator: TranslatorFunction,
    enumOptions: FieldOption[],
  ): Array<{ key: string; label: string }> {
    return enumOptions
      .map(o => ({
        key: o.key,
        label: translator(o.label) || o.fallbackLabel,
      }))
      .sort(OnboardingFieldComponent.sortByLabel);
  }

  static enumOptionsToBubbleUiForArrayField(
    translator: TranslatorFunction,
    enumOptions: FieldOption[],
  ): Array<{ key: string; label: string }> {
    return enumOptions.map(o => ({
      key: o.key,
      label: translator(o.label) || o.fallbackLabel,
    }));
  }
}
